import { useEffect, useMemo, useState } from "react";
import { Plus, Minus, Trash, Receipt, Printer } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";
import { zar, fmtTime } from "../lib/format";
import { ReceiptPrintBlock } from "./KioskPos";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

export default function PosTables() {
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState([]);
  const [shift, setShift] = useState(null);
  const [order, setOrder] = useState(null);
  const [closing, setClosing] = useState(false);
  const [tip, setTip] = useState("");
  const [receipt, setReceipt] = useState(null);

  const load = () => {
    api.get("/pos/tables").then((r) => setTables(r.data.filter((t) => !t.archived))).catch(() => {});
    api.get("/pos/orders?status=open").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/pos/menu").then((r) => setMenu(r.data.filter((m) => !m.archived))).catch(() => {});
    api.get("/pos/shifts/current").then((r) => setShift(r.data.id ? r.data : null)).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(() => api.get("/pos/orders?status=open").then((r) => setOrders(r.data)).catch(() => {}), 15000);
    return () => clearInterval(t);
  }, []);

  const openOrder = orders.find((o) => order && o.id === order.id);
  const byTable = useMemo(() => Object.fromEntries(orders.map((o) => [o.table_id, o])), [orders]);
  const menuByCat = useMemo(() => {
    const g = {};
    menu.forEach((m) => {
      const top = (m.category_path || "Other").split(" / ")[0];
      (g[top] = g[top] || []).push(m);
    });
    return g;
  }, [menu]);

  const openTable = async (t) => {
    try {
      const { data } = await api.post("/pos/orders", { table_id: t.id });
      setOrder(data);
      setReceipt(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Could not open table"));
    }
  };

  const saveLines = async (lines) => {
    try {
      const { data } = await api.put(`/pos/orders/${order.id}/lines`, { lines });
      setOrder(data);
      load();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    }
  };

  const addItem = (mi) => {
    const lines = order.lines.map((l) => ({ menu_item_id: l.menu_item_id, qty: l.qty }));
    const ex = lines.find((l) => l.menu_item_id === mi.id);
    if (ex) ex.qty += 1; else lines.push({ menu_item_id: mi.id, qty: 1 });
    saveLines(lines);
  };

  const bump = (lineId, delta) => {
    const lines = order.lines
      .map((l) => ({ menu_item_id: l.menu_item_id, qty: l.id === lineId ? l.qty + delta : l.qty }))
      .filter((l) => l.qty > 0);
    saveLines(lines);
  };

  const voidOrder = async () => {
    try {
      await api.post(`/pos/orders/${order.id}/void`);
      toast.success("Order voided");
      setOrder(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Void failed"));
    }
  };

  const closeOut = async () => {
    try {
      const { data } = await api.post(`/pos/orders/${order.id}/close`, { tip: Number(tip) || 0 });
      setReceipt(data);
      setClosing(false);
      setTip("");
      load();
      if (data.unlinked_items?.length) {
        toast.warning(`No recipe linked — stock NOT depleted for: ${data.unlinked_items.join(", ")}`);
      }
      if (data.depletion?.movements_created) {
        toast.success(`Stock depleted (${data.depletion.movements_created} movement(s)) — revenue & tip posted`);
      } else {
        toast.success("Receipt closed — revenue & tip posted");
      }
    } catch (e) {
      toast.error(apiError(e, "Close-out failed"));
    }
  };

  return (
    <div className="space-y-6" data-testid="pos-tables-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Tables</h1>
          <p className="text-muted-foreground mt-1">
            {shift ? (
              <>Shift open since {fmtTime(shift.opened_at)} by {shift.opened_by}</>
            ) : (
              <>No shift open — receipts still post in real time. <span className="text-xs">(Owner/Manager opens shifts under POS → Shifts)</span></>
            )}
          </p>
        </div>
        <Badge variant={shift ? "default" : "outline"} className={shift ? "bg-[#4A5D23] text-white" : ""} data-testid="pos-shift-state">
          {shift ? "Shift open" : "No shift"}
        </Badge>
      </div>

      {tables.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="pos-no-tables">
          No tables configured yet — an Owner/Manager adds them under POS → Menu Builder → Tables.
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {tables.map((t) => {
          const o = byTable[t.id];
          return (
            <button key={t.id} onClick={() => openTable(t)} data-testid={`table-${t.id}`}
              className={`rounded-lg border p-5 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm ${
                o ? "bg-[#A78BFA]/5 border-[#A78BFA]/40" : "bg-white border-border"}`}>
              <p className="font-heading font-semibold text-lg">{t.name}</p>
              {o ? (
                <p className="text-xs mt-1 text-[#A78BFA] font-medium num" data-testid={`table-open-${t.id}`}>
                  Open · {o.lines.reduce((a, l) => a + l.qty, 0)} item(s) · {zar(o.subtotal)}
                </p>
              ) : (
                <p className="text-xs mt-1 text-muted-foreground">Available</p>
              )}
            </button>
          );
        })}
      </div>

      <Dialog open={!!order && !receipt} onOpenChange={() => { setOrder(null); setClosing(false); }}>
        <DialogContent className="max-w-3xl" data-testid="order-dialog">
          {openOrder && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center justify-between">
                  <span>{openOrder.table_name} — Order</span>
                  <span className="text-sm font-normal text-muted-foreground">opened {fmtTime(openOrder.opened_at)} by {openOrder.opened_by}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-5">
                <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Menu</p>
                  {Object.entries(menuByCat).map(([cat, items]) => (
                    <div key={cat}>
                      <p className="text-xs font-semibold text-muted-foreground mb-1">{cat}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {items.map((m) => (
                          <button key={m.id} onClick={() => addItem(m)} data-testid={`menu-add-${m.id}`}
                            className="rounded-md border border-border bg-white px-2.5 py-1.5 text-xs hover:bg-accent transition-colors">
                            {m.name} <span className="num text-muted-foreground">{zar(m.price)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {menu.length === 0 && <p className="text-sm text-muted-foreground">Menu is empty — build it under POS → Menu Builder.</p>}
                </div>
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Order</p>
                  {openOrder.lines.length === 0 && <p className="text-sm text-muted-foreground">Nothing yet — tap menu items to add.</p>}
                  {openOrder.lines.map((l) => (
                    <div key={l.id} className="flex items-center gap-2 text-sm" data-testid={`order-line-${l.id}`}>
                      <span className="flex-1 font-medium">{l.name}</span>
                      <button onClick={() => bump(l.id, -1)} data-testid={`line-minus-${l.id}`} className="p-1 rounded hover:bg-accent"><Minus size={13} /></button>
                      <span className="num w-6 text-center">{l.qty}</span>
                      <button onClick={() => bump(l.id, 1)} data-testid={`line-plus-${l.id}`} className="p-1 rounded hover:bg-accent"><Plus size={13} /></button>
                      <span className="num w-20 text-right">{zar(l.line_total)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between border-t pt-3 font-semibold">
                    <span>Subtotal</span>
                    <span className="num" data-testid="order-subtotal">{zar(openOrder.subtotal)}</span>
                  </div>
                  {closing ? (
                    <div className="space-y-3 border-t pt-3" data-testid="closeout-panel">
                      <div className="space-y-1.5">
                        <Label>Gratuity / tip (R)</Label>
                        <Input type="number" min="0" step="any" data-testid="closeout-tip" value={tip} onChange={(e) => setTip(e.target.value)} autoFocus />
                      </div>
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total</span>
                        <span className="num" data-testid="closeout-total">{zar(openOrder.subtotal + (Number(tip) || 0))}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">Payment is taken on the card machine — marking paid posts the sale + tip and depletes stock for recipe-linked items.</p>
                      <div className="flex gap-2">
                        <Button onClick={closeOut} data-testid="closeout-confirm" className="flex-1 bg-primary hover:bg-[#8B5CF6] text-white">
                          <Receipt size={15} className="mr-1" /> Mark paid
                        </Button>
                        <Button variant="outline" onClick={() => setClosing(false)} data-testid="closeout-cancel">Back</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button onClick={() => setClosing(true)} disabled={!openOrder.lines.length} data-testid="order-close-btn"
                        className="flex-1 bg-primary hover:bg-[#8B5CF6] text-white">Close out…</Button>
                      <Button variant="outline" onClick={voidOrder} data-testid="order-void-btn"
                        className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive">
                        <Trash size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!receipt} onOpenChange={() => { setReceipt(null); setOrder(null); }}>
        <DialogContent className="max-w-sm" data-testid="receipt-dialog">
          {receipt && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-center">Receipt {receipt.receipt_no}</DialogTitle></DialogHeader>
              <div className="text-center text-xs text-muted-foreground -mt-2" data-testid="receipt-meta">
                {receipt.table_name} · {receipt.closed_by} · {fmtTime(receipt.closed_at)}
              </div>
              <div className="font-mono text-sm border-y border-dashed py-3 my-3 space-y-1.5" data-testid="receipt-lines">
                {receipt.lines.map((l) => (
                  <div key={l.id} className="flex justify-between">
                    <span>{l.qty} × {l.name}</span><span className="num">{zar(l.line_total)}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Subtotal</span><span className="num" data-testid="receipt-subtotal">{zar(receipt.subtotal)}</span></div>
                <div className="flex justify-between"><span>Gratuity</span><span className="num" data-testid="receipt-tip">{zar(receipt.tip)}</span></div>
                <div className="flex justify-between font-bold text-base border-t pt-1"><span>Total</span><span className="num" data-testid="receipt-total">{zar(receipt.total)}</span></div>
              </div>
              {receipt.unlinked_items?.length > 0 && (
                <p className="text-xs text-[#8a6414] bg-[#D99A29]/10 rounded px-2 py-1.5 mt-2" data-testid="receipt-unlinked">
                  Not depleted (no recipe linked): {receipt.unlinked_items.join(", ")}
                </p>
              )}
              <div className="flex gap-2 mt-2">
                <Button variant="outline" data-testid="receipt-print" className="flex-1"
                  onClick={async () => {
                    try {
                      const { data } = await api.post(`/pos/orders/${receipt.id}/print`);
                      toast.success(data.queued ? "Queued — bridge will print shortly" : "Sent to receipt printer");
                    } catch (e) {
                      if (e.response?.status === 409) {
                        window.print();  // no printer configured — browser print fallback
                      } else {
                        toast.error(apiError(e, "Print failed — trying browser print"));
                        window.print();
                      }
                    }
                  }}>
                  <Printer size={15} className="mr-1" /> Print
                </Button>
                <Button onClick={() => { setReceipt(null); setOrder(null); }} data-testid="receipt-done" className="flex-1 bg-primary hover:bg-[#8B5CF6] text-white">Done</Button>
              </div>
              <ReceiptPrintBlock receipt={receipt} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
